import { type SetOptional } from 'type-fest';

import {
  type CanvasElementId,
  type CanvasElementType,
  type CollapsibleContainerBehaviorExpression,
} from '@blaze-runtime/engine-domain';
import { ComparisonOperator } from '@blaze-shared/api-contracts-domain';
import {
  type InternalVisibilityExpressionState,
  type VisibilityExpressionState,
} from '@blaze-shared/datasets-domain';
import {
  type CollapsibleContainerBehaviorModel,
  type ExpressionObj,
  type PropertiesConfigInputVisibilityModel,
  type RuntimeContext,
  type TableConditionalFormatExpression,
  type TableConditionalFormatModel,
} from '@blaze-shared/elements-domain';
import { type IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { logPrint } from '@blaze-shared/logger-util';
import { type DatasetColumn } from '@blaze-shared/schema-domain';

interface SingleCondition {
  columns: string[];
  operator: ComparisonOperator;
  value: any;
}

export function inputVisibilityModelToExpression(
  model: PropertiesConfigInputVisibilityModel,
): ExpressionObj[] {
  const expression = [
    ...(model?.internalStates?.length
      ? convertInternalStateToExpression(model.internalStates)
      : []),
    ...(model?.conditions?.[0] ? convertVisibilityStateToExpression(model) : []),
  ];
  return expression;
}

export const convertVisibilityStateToExpression = (value: {
  states?: VisibilityExpressionState[];
  conditions: SetOptional<SingleCondition, 'value'>[];
  datasetColumns?: { columns?: DatasetColumn; value?: DatasetColumn }[];
}): ExpressionObj[] => {
  return (
    value.states?.map((state, index) => {
      return {
        state,
        columns: value.conditions[index]?.columns?.[0],
        operator: value.conditions[index]?.operator,
        value: value.conditions[index]?.value,
        columnsDataset: value.datasetColumns?.[index]?.columns,
        valueDataset: value.datasetColumns?.[index]?.value,
      };
    }) || []
  );
};

export const convertCollapsibleContainerBehaviorStateToExpression = (
  value: CollapsibleContainerBehaviorModel,
): CollapsibleContainerBehaviorExpression[] => {
  return (
    value.states?.map((state, index) => {
      return {
        state,
        columns: value.conditions?.[index]?.columns?.[0] ?? '',
        operator: value.conditions?.[index]?.operator ?? ComparisonOperator.Eq,
        value: value.conditions?.[index]?.value ?? '',
        columnsDataset: value.datasetColumns?.[index]?.columns,
        valueDataset: value.datasetColumns?.[index]?.value,
      };
    }) || []
  );
};

export const convertTableConditionalFormatStateToExpression = (
  value: TableConditionalFormatModel,
): TableConditionalFormatExpression[] => {
  return (
    value.states?.map((state, index) => {
      return {
        state,
        columns: value.conditions?.[index]?.columns?.[0] ?? '',
        operator: value.conditions?.[index]?.operator ?? ComparisonOperator.Eq,
        value: value.conditions?.[index]?.value ?? '',
        columnsDataset: value.datasetColumns?.[index]?.columns,
        valueDataset: value.datasetColumns?.[index]?.value,
      };
    }) || []
  );
};

export const convertInternalStateToExpression = (
  states: InternalVisibilityExpressionState[],
): ExpressionObj[] => {
  return states.map((state) => {
    // Only "hideWhenNoData" is currently handled
    return {
      internalState: state,
      columns: '$$inputValue["length"]',
      operator: ComparisonOperator.Gt,
      value: '0',
      columnsDataset: undefined,
      valueDataset: undefined,
    };
  });
};

export function sanitizeConditions(
  query: PropertiesConfigInputVisibilityModel,
): PropertiesConfigInputVisibilityModel {
  if (query && query.conditions.length === 1) {
    const first = query.conditions[0];
    if (first.value === 'true' && Object.keys(first).length === 1) {
      return { ...query, conditions: [] };
    }
  }
  return query;
}

export interface EvaluateExpressionParams {
  expression: any;
  context: RuntimeContext;
  intellisense: IntelliSenseService;
  elementId: CanvasElementId;
  elementType: CanvasElementType;
  ignoreNormalization?: boolean;
  isVisibility?: boolean;
}

export function evaluateExpression({
  expression,
  context,
  intellisense,
  elementId,
  elementType,
  ignoreNormalization = false,
  isVisibility = true,
}: EvaluateExpressionParams): any {
  const result = intellisense.evalExpressionObj(expression, context, ignoreNormalization);
  if (isVisibility) {
    logPrint('visible_eval', 'info', 'CanvasElementBaseComponent', 'evaluateExpression', (tag) =>
      console.log(`${tag} [visible] ${elementId} | ${elementType} → ${result}`, {
        expression,
        context,
      }),
    );
  }
  return result;
}
